import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
    Box,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Typography,
    Grid,
    Paper,
    MenuItem,
    Snackbar,
    Alert,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import withLanguage from "../../HOC/withLanguage";
import withAuthChecker from "../../HOC/withAuthChecker";
import CustomTable from "./ArsimiDualComponents/CustomTable";
import {formatToDateInput, isAtLeastYears, fixEncoding} from "./../Shared/helperFunctions"

let pageSize = 5;

const SubjectDeclaration = ({ language }) => {
  const [submitting, setSubmitting] = React.useState(false);
    const user = useSelector((state) => state.user);
    const [subjects, setSubjects] = React.useState(null);
    const [schoolYears, setSchoolYears] = React.useState([]);
    const [qualificationOptions, setQualificationOptions] = React.useState([]);
    
    const [showForm, setShowForm] = React.useState(false);
    const [readOnlyForm, setReadOnlyForm] = React.useState(false);

    const [page, setPage] = React.useState(0);

    // Snackbar for success/error feedback
    const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const columns = [
      {
        header: language?.sd_nipt,
        accessor: "nipt",
        cellClassName:
          "border-b border-slate-100 px-4 py-4 text-sm font-medium text-slate-800",
      },
      {
        header: language?.sd_subjectName,
        accessor: "employer_name",
        cellClassName:
          "border-b border-slate-100 px-4 py-4 text-sm text-slate-700",
      },
      {
        header: language?.sd_registrationDate,
        accessor: "year_of_enrollment",
        isDate: true,
        cellClassName:
          "border-b border-slate-100 px-4 py-4 text-sm text-slate-700",
      },
      {
        header: language?.sd_status,
        accessor: "state",
        cellClassName:
          "border-b border-slate-100 px-4 py-4 text-sm text-slate-700",
        render: (item) => language?.status?.[item.state] || item.state,
      },
      {
        header: language?.sd_relectedReason,
        accessor: "reason",
        cellClassName:
          "border-b border-slate-100 px-4 py-4 text-sm text-slate-700",
        render: (item) => item?.reason ? item?.reason : '-',
      },
      {
        header: "",
        headerClassName: "text-right",
        cellClassName: "border-b border-slate-100 px-4 py-4 text-right",
        render: (item) => (
          <button
            className="inline-flex border-0 bg-transparent text-[#dc2626]"
            onClick={() => showSubjectDetails(item)}
          >
            <ArrowForwardIcon />
          </button>
        ),
      },
    ];
    
    const getSubjectsData = () => {
      if(!user?.nid) return
        axios.get(`/api/person_search/${user?.nid}?page=${page+1}&per_page=${pageSize}`, {
            headers: { "API_KEY": "123456789" },
        })
        .then((res) => {
            if(res?.data?.found) {
                setSubjects(res?.data)
            } 
        })
        .catch((err) => {
            setSubjects(null)
        });
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        getSubjectsData()
    }, [user, page, pageSize])


    // Subject Declaration default state factory
    const defaultSd = () => ({
        applicant_id: user?.nid,
        nipt: "",
        employerName: "",
        fieldOfActivity: "",
        yearOfEnrollment: "",
        employeesEsig: "",
        schoolYearId: "",
        mentorInfraAvailable: false,

        qualifications: [
            {
                dualQualificationId: "",
                dualVacancies: "",
                mentors: [
                    {
                        mentorNid: "",
                        mentorFullName: "",
                    },
                ],
            },
        ]
    });
    const [sd, setSd] = React.useState(defaultSd());
    

    /* ---------- top-level handlers ---------- */
    const sdOnChange = (e) => {
        const { name, value, type, checked } = e.target;
        if(name==='nipt'){
            setSd((s) => ({
                ...s,
                employerName: "",
                fieldOfActivity: "",
                yearOfEnrollment: ""
            }));
        }

        setSd((s) => ({
            ...s,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    /* ---------- qualification rows (B1) ---------- */
    const sdAddQualification = () => {
        setSd((s) => ({
            ...s,
            qualifications: [
                ...s.qualifications,
                {
                    dualQualificationId: "",
                    dualVacancies: "",
                    mentors: [
                        {
                            mentorNid: "",
                            mentorFullName: "",
                        },
                    ],
                },
            ],
        }));
    };

    const sdRemoveQualification = (qi) => {
        setSd((s) => ({
            ...s,
            qualifications: s.qualifications.filter((_, i) => i !== qi),
        }));
    };

    const sdSetQualificationField = (qi, field, val) => {
        setSd((s) => {
            const quals = [...s.qualifications];
            quals[qi] = { ...quals[qi], [field]: val };
            return { ...s, qualifications: quals };
        });
    };

    /* ---------- mentor rows per qualification (B2) ---------- */
    const sdAddMentor = (qualificationIndex) => {
        setSd((s) => {
            const qualifications = [...s.qualifications];
            const q = { ...qualifications[qualificationIndex] };
            q.mentors = [
                ...q.mentors,
                {
                    mentorNid: "",
                    mentorFullName: "",
                },
            ];
            qualifications[qualificationIndex] = q;
            return { ...s, qualifications };
        });
    };

    const sdRemoveMentor = (qualificationIndex, mentorIndex) => {
        setSd((s) => {
            const qualifications = [...s.qualifications];
            const q = { ...qualifications[qualificationIndex] };
            q.mentors = q.mentors.filter((_, i) => i !== mentorIndex);
            qualifications[qualificationIndex] = q;
            return { ...s, qualifications };
        });
    };

    const sdSetMentorField = (qualificationIndex, mentorIndex, field, val) => {
        setSd((s) => {
            const qualifications = [...s.qualifications];
            const q = { ...qualifications[qualificationIndex] };
            const mentors = [...q.mentors];
            mentors[mentorIndex] = { ...mentors[mentorIndex], [field]: val };
            q.mentors = mentors;
            qualifications[qualificationIndex] = q;
            return { ...s, qualifications };
        });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);

        const payload = {
            
            applicant_id: user?.nid || "",
            nipt: sd.nipt || "",
            employer_name: sd.employerName || "",
            field_of_activity: sd.fieldOfActivity || "",
            year_of_enrollment: sd.yearOfEnrollment || "",
            employees_esig: sd.employeesEsig ? Number(sd.employeesEsig) : 0,
            school_year_id: sd.schoolYearId ? Number(sd.schoolYearId) : "",
            mentor_availability: !!sd.mentorInfraAvailable,
            infrastructure_provided: !!sd.mentorInfraAvailable,

            qualification_ioap_ids: sd.qualifications.map((q) => ({
                dual_qualification_id: q.dualQualificationId || null,
                dual_vacancies: q.dualVacancies ? Number(q.dualVacancies) : 0,
                mentor_ioap_ids: q.mentors.map((m) => ({
                    nid: m.mentorNid || "",
                    mentor_full_name: m.mentorFullName || "",
                })),
            })),
        };

        if(!isAtLeastYears(payload?.year_of_enrollment, 3)){
          setSnackbar({ open: true, message: language.sd_registrationYearError, severity: "error" });
          setSubmitting(false);
          return
        }

        if(!payload.applicant_id || !payload.nipt || !payload.field_of_activity || !payload.year_of_enrollment || !payload.employees_esig || !payload.school_year_id || !(payload.mentor_availability && payload.infrastructure_provided)){
          setSnackbar({ open: true, message: language.requiredFields, severity: "error" });
          setSubmitting(false);
          return
        }

        fetch("/api/employer_declaration", {
            method: "POST",
            headers: { "API_KEY": "123456789", "Content-Type": "text/plain" },
            body: JSON.stringify(payload),
            
        })
            .then(async (r) => {
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    throw new Error(
                        data?.message || language.requestFailed
                    );
                }
                setSnackbar({ open: true, message: language.submitSuccess, severity: "success" });
                setSd(defaultSd());
                setSubmitting(false);
            })
            .catch((err) => {
                setSnackbar({
                    open: true,
                    message: err.message || language.error,
                    severity: "error",
                });
                setSubmitting(false);
            });
    };

    const showSubjectDetails = (subject) => {
        setReadOnlyForm(true);
        setShowForm(true);

        setSd((s) => ({
            ...s,
            applicant_id: subject?.nid ?? "",
            nipt: subject?.nipt ?? "",
            employerName: fixEncoding(subject?.employer_name) ?? "",
            fieldOfActivity: fixEncoding(subject?.field_of_activity) ?? "",
            yearOfEnrollment: subject?.year_of_enrollment ?? "",
            employeesEsig: subject?.employees_esig ?? "",
            schoolYearId: subject?.school_year_id,
            state: subject?.state,
            mentorInfraAvailable:
            !!subject?.mentor_availability && !!subject?.infrastructure_provided,

            qualifications: (subject?.qualification_ioap_ids ?? []).map((item) => ({
            dualQualificationId: item?.dual_qualification_id ?? "",
            dualVacancies: item?.dual_vacancies ?? "",
            mentors: (item?.mentor_ioap_ids ?? [{ mentorNid: "", mentorFullName: "" }]).map((mentor) => ({
                mentorNid: mentor?.nid ?? "",
                mentorFullName: mentor?.mentor_full_name ?? "",
                state: mentor?.state ?? "",
            })),
            })),
        }));

    };


    /* ---------- effects: load dropdown data ---------- */
    React.useEffect(() => {
        if (!showForm || !subjects?.institution_id) return;

        axios.get(`/api/institution_configuration/degree?institution_id__exact=${subjects?.institution_id}&dual_education__exact=true`, { 
            headers: { API_KEY: "123456789" },
        })
            .then((res) => {
                const data = res.data;
                if (Array.isArray(data)) {
                    setQualificationOptions(data);
                } else {
                    setQualificationOptions([]);
                }
            })
            .catch(() => {
                setQualificationOptions([]);
            });

    }, [showForm, subjects]);

    React.useEffect(() => {
        axios.get(`/api/data/school_year?is_active__exact=true`, { 
            headers: { API_KEY: "123456789" },
        })
            .then((res) => {
                const data = res.data;
                setSchoolYears(data);
                
            })
            .catch(() => {
                setSchoolYears([]);
            });

    }, [showForm]);


    const niptValidation = (nipt) => {
        if (!nipt) return;

        axios
            .get(`/api/validate_nipt/${nipt}`, {
            headers: { API_KEY: "123456789" },
            })
            .then((res) => {
                if (!res.data?.found) {
                    throw new Error(language.sd_invalidNipt);
                }

                if (res.data?.found) {
                    setSd((s) => ({
                        ...s,
                        employerName: fixEncoding(res.data.nipt_data?.name),
                        fieldOfActivity: fixEncoding(res.data.nipt_data?.activity_field),
                        yearOfEnrollment: formatToDateInput(res.data.nipt_data?.registration_date),
                    }));

                    if (!isAtLeastYears(res.data.nipt_data?.registration_date, 3)) {
                      setSnackbar({ open: true, message: language.sd_registrationYearError, severity: "error" });
                    } 
                }
            })
            .catch((err) => {
              const message =
                    err.response?.data?.message || 
                    err?.message ||
                    language.error

                setSnackbar({
                    open: true,
                    message,
                    severity: "error",
                });
            });
    };

    const preventInvalidNumberInput = (e) => {
      const invalidKeys = ["-", "+", "e", "E"];

      if (invalidKeys.includes(e.key)) {
        e.preventDefault();
      }
    };


    return (
      <section className="flex flex-1 items-center">
        {!showForm ? (
          <Paper elevation={0} sx={{ width: "100%" }}>
            <Grid
              sx={{
                position: "relative",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                width: "100%",
                py: 6,
                pt: 0,
              }}
            >
              {subjects?.data?.records?.length > 0 && (
                <>
                  <button
                    type="button"
                    className="inline-flex border-0 bg-transparent text-[#dc2626] absolute top-10 right-9"
                    onClick={() => {
                      setPage(0);
                      getSubjectsData();
                    }}
                  >
                    <RefreshIcon />
                  </button>
                  <CustomTable
                    title={`${language?.sd_subjects}:`}
                    data={subjects.data.records || []}
                    columns={columns}
                    pagination={{
                      count: subjects.data?.amount,
                      pageSize,
                      page,
                      onPageChange: handleChangePage,
                    }}
                  />
                </>
              )}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 700, fontSize: "1.8rem" }}
                >
                  {language?.sd_openSubtitle}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="button"
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    borderRadius: "1.5rem",
                    padding: "0.4rem 1rem",
                    borderColor: "#e5e7eb",
                    color: "#fff",
                    fontWeight: "bold",
                    backgroundColor: "#dc2626",
                    fontSize: "1rem",
                    "&:hover": {
                      backgroundColor: "#dc2626",
                      borderColor: "#b91c1c",
                    },
                  }}
                  onClick={() => {
                    setShowForm(true);
                    setSd(defaultSd());
                    setReadOnlyForm(false);
                  }}
                >
                  {language?.sd_declare}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <div>
            <Button
              type="button"
              variant="outlined"
              sx={{
                textTransform: "none",
                marginBottom: "2rem",
                fontWeight: "bold",
                borderColor: "transparent",
                color: "#dc2626",
                backgroundColor: "transparent",
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                },
              }}
              onClick={() => {
                getSubjectsData();
                setShowForm(false);
              }}
            >
              <ArrowBackIcon className="mr-5" />
              {language?.backToSubjectsList}
            </Button>
            <Box
              component="form"
              onSubmit={onSubmit}
              sx={{ display: "grid", gap: 2 }}
            >
              {/* ===== Seksioni A ===== */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {language.sd_titleA}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "15px" },
                      }}
                      label={language.sd_nipt}
                      name="nipt"
                      value={sd.nipt}
                      onChange={sdOnChange}
                      fullWidth
                      required
                      disabled={readOnlyForm}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Button
                      type="button"
                      onClick={() => niptValidation(sd.nipt)}
                      variant="outlined"
                      disabled={!sd.nipt || readOnlyForm}
                    >
                      {language.sd_validate}
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "15px" },
                      }}
                      label={language.sd_employerName}
                      name="employerName"
                      value={sd.employerName}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: true }}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "15px" },
                      }}
                      label={language.sd_activityField}
                      name="fieldOfActivity"
                      value={sd.fieldOfActivity}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: true }}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "15px" },
                      }}
                      type="date"
                      name="yearOfEnrollment"
                      onChange={sdOnChange}
                      label={language.sd_registrationYear}
                      value={sd.yearOfEnrollment}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: true }}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "15px" },
                      }}
                      type="number"
                      label={language.sd_employeesEsig27}
                      name="employeesEsig"
                      value={sd.employeesEsig}
                      onChange={sdOnChange}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onKeyDown={(e) => preventInvalidNumberInput(e)}
                      required
                      disabled={readOnlyForm}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                        },
                      }}
                      select
                      name="schoolYearId"
                      label={language.sd_schoolYear}
                      value={sd.schoolYearId}
                      onChange={sdOnChange}
                      fullWidth
                      required
                      disabled={readOnlyForm}
                    >
                      {schoolYears.map((year) => (
                        <MenuItem
                          key={year.id ?? year.ID ?? year.value}
                          value={year.id ?? year.ID ?? year.value}
                        >
                          {year.name ?? year.emri ?? year.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {readOnlyForm && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                          },
                        }}
                        select
                        name="state"
                        label={language.sd_status}
                        value={sd.state}
                        onChange={sdOnChange}
                        fullWidth
                        required
                        disabled={readOnlyForm}
                      >
                        {Object.keys(language?.status).map((item) => (
                          <MenuItem
                            key={item}
                            value={item}
                          >
                            {language?.status?.[item]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={sd.mentorInfraAvailable}
                          sx={{ "&.Mui-checked": { color: "#dc2626" } }}
                          onChange={(e) =>
                            sdOnChange({
                              target: {
                                name: "mentorInfraAvailable",
                                type: "checkbox",
                                checked: e.target.checked,
                              },
                            })
                          }
                          disabled={readOnlyForm}
                        />
                      }
                      label={language.sd_hasMentors}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* ===== Seksioni B ===== */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  {language.sd_titleB}
                </Typography>

                {/* --- B1: Kualifikimet (qualification_ioap_ids) --- */}
                {sd.qualifications.map((q, qi) => (
                  <Paper
                    key={`qual-block-${qi}`}
                    variant="outlined"
                    sx={{ p: 2, mb: 3, bgcolor: "#fafafa" }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 2 }}>
                      {language.sd_qualification} #{qi + 1}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "15px",
                            },
                          }}
                          select
                          label={language.sd_qualification}
                          value={q.dualQualificationId}
                          onChange={(e) =>
                            sdSetQualificationField(
                              qi,
                              "dualQualificationId",
                              e.target.value,
                            )
                          }
                          fullWidth
                          required
                          disabled={readOnlyForm}
                        >
                          {qualificationOptions.map((opt) => (
                            <MenuItem
                              key={opt.id ?? opt.ID ?? opt.value}
                              value={opt.id ?? opt.ID ?? opt.value}
                            >
                              {opt.name ?? opt.emri ?? opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "15px",
                            },
                          }}
                          type="number"
                          label={language.sd_dualVacancies}
                          value={q.dualVacancies}
                          onChange={(e) =>
                            sdSetQualificationField(
                              qi,
                              "dualVacancies",
                              e.target.value,
                            )
                          }
                          disabled={readOnlyForm}
                          onKeyDown={(e) => preventInvalidNumberInput(e)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Mentorë
                      </Typography>

                      {q.mentors.map((m, mi) => (
                        <Paper
                          key={`mentor-${qi}-${mi}`}
                          variant="outlined"
                          sx={{ p: 2, mb: 2 }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                required
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "15px",
                                  },
                                }}
                                label="NID"
                                value={m.mentorNid}
                                onChange={(e) =>
                                  sdSetMentorField(
                                    qi,
                                    mi,
                                    "mentorNid",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                disabled={readOnlyForm}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "15px",
                                  },
                                }}
                                label={language.sd_mentorFullName}
                                value={m.mentorFullName}
                                onChange={(e) =>
                                  sdSetMentorField(
                                    qi,
                                    mi,
                                    "mentorFullName",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                required
                                disabled={readOnlyForm}
                              />
                            </Grid>

                             {readOnlyForm && (
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "15px",
                                      },
                                    }}
                                    select
                                    name="state"
                                    label={language.sd_status}
                                    value={m.state}
                                    fullWidth
                                    disabled={readOnlyForm}
                                  >
                                    {Object.keys(language?.mentorStatus).map((item) => (
                                      <MenuItem
                                        key={item}
                                        value={item}
                                      >
                                        {language?.mentorStatus?.[item]}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                              )}

                            <Grid
                              item
                              xs={12}
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              {q.mentors.length > 1 && (
                                <Button
                                  type="button"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  disabled={readOnlyForm}
                                  onClick={() => sdRemoveMentor(qi, mi)}
                                >
                                  {language.sd_removeMentor}
                                </Button>
                              )}
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}

                      <Button
                        type="button"
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          color: "#dc2626",
                          borderColor: "#b91c1c",
                          borderWidth: 1.5,
                          borderRadius: "0.7rem",
                          padding: "0.3rem 1.2rem",
                          fontSize: "0.96rem",
                          "&:hover": {
                            borderColor: "#b91c1c",
                            color: "#b91c1c",
                          },
                        }}
                        disabled={readOnlyForm}
                        onClick={() => sdAddMentor(qi)}
                      >
                        {language.sd_addMentor}
                      </Button>
                    </Paper>

                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      {sd.qualifications.length > 1 && (
                        <Button
                          type="button"
                          color="error"
                          variant="outlined"
                          disabled={readOnlyForm}
                          onClick={() => sdRemoveQualification(qi)}
                        >
                          {language.sd_removeQual}
                        </Button>
                      )}
                    </Grid>
                  </Paper>
                ))}

                {/* --- B2: Mentorët (mentor_ioap_ids) --- */}

                <Button
                  type="button"
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    color: "#dc2626",
                    borderColor: "#b91c1c",
                    borderWidth: 1.5,
                    borderRadius: "0.7rem",
                    padding: "0.3rem 1.2rem",
                    fontSize: "0.96rem",
                    "&:hover": { borderColor: "#b91c1c", color: "#b91c1c" },
                  }}
                  disabled={readOnlyForm}
                  onClick={sdAddQualification}
                >
                  {language.sd_addQual}
                </Button>
              </Paper>

              {/* ===== Submit ===== */}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    textTransform: "none",
                    bgcolor: "#dc2626",
                    "&:hover": { bgcolor: "#b91c1c" },
                  }}
                  disabled={readOnlyForm || submitting}
                >
                  {language.submit}
                </Button>
              </Box>
            </Box>
          </div>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </section>
    );
};


export default withAuthChecker(withLanguage(SubjectDeclaration, "SubjectDeclaration"), 5);